import { checkKeyMainRoute, getMainRoute } from '@/utils/Routes';
import { walletHandoffUrl } from '@/utils/Wallet/handoff';

import { UserMeProps } from '@/services/User/types';

import { comMarcaDeSso } from '@/lib/idp/marca-sso';
import { destinoPosLogin as deepLinkValido } from '@/lib/idp/paths';

/**
 * Para onde o usuario vai depois de entrar — uma regra so, para o login por
 * senha e para o "Entrar com Seazone".
 *
 * Existe porque os dois caminhos tinham cada um a sua conta e divergiam: pela
 * senha o Owner+Admin ia para a Wallet e o deep-link era preservado; pelo IDP o
 * mesmo usuario ficava no `/home` e o deep-link se perdia. O caminho por senha e
 * a referencia (decisao de produto), entao a ordem abaixo e a dele:
 *
 * 1. Host/CoHost com o redirecionamento para o Franquias ligado → Franquias
 *    (nos dois caminhos: o Franquias e client do realm e aceita a marca de SSO);
 * 2. quem tem a role `Owner` (inclusive Owner+Admin) → Wallet;
 * 3. o deep-link guardado antes do login, quando ele e destino util;
 * 4. a rota principal do papel (`getMainRoute`).
 */
export type DestinoPosLogin =
  | { readonly tipo: 'franquias' }
  | { readonly tipo: 'wallet' }
  | { readonly tipo: 'rota'; readonly rota: string };

export interface EntradaDoDestino {
  readonly usuario: UserMeProps;
  /** Path + query + hash pedido antes do login (o `state.from` do `RequireAuth`). */
  readonly deepLink?: string;
  /** Resultado da flag de redirecionamento para o Franquias (`vaiParaFranquias`). */
  readonly franquias: boolean;
}

export function ehHostOuCoHost(usuario: UserMeProps): boolean {
  const roles = usuario.roles ?? [];
  return roles.includes('Host') || roles.includes('HostProfile');
}

export function resolverDestinoPosLogin({
  usuario,
  deepLink,
  franquias,
}: EntradaDoDestino): DestinoPosLogin {
  if (franquias && ehHostOuCoHost(usuario)) return { tipo: 'franquias' };
  if ((usuario.roles ?? []).includes('Owner')) return { tipo: 'wallet' };

  const rota = deepLinkValido(deepLink);
  if (rota !== undefined) return { tipo: 'rota', rota };

  return { tipo: 'rota', rota: getMainRoute[checkKeyMainRoute(usuario)] };
}

export interface SaidaDoApp {
  readonly walletUrl?: string;
  readonly franquiasUrl?: string;
  /** A sessao veio do IDP: nenhum token atravessa a URL. */
  readonly comSessaoDoIdp: boolean;
  /** Access token da sessao legada, so para o repasse por URL. */
  readonly accessToken?: string;
}

/**
 * A URL de fora do app para um destino externo, ou `undefined` quando o destino
 * e uma rota do proprio Sapron.
 *
 * Com sessao do IDP o token nunca vai na URL (proibicao numero um do contrato
 * do IDP): a Wallet e o Franquias sao clients do mesmo realm e recebem so a
 * marca `?sso=1` (`lib/idp/marca-sso.ts`), entrando pela sessao SSO do
 * Keycloak. Na sessao legada o repasse por `?token=` continua como esta.
 */
export function urlDoDestino(destino: DestinoPosLogin, saida: SaidaDoApp): string | undefined {
  if (destino.tipo === 'wallet') {
    return walletHandoffUrl({
      walletUrl: saida.walletUrl,
      comSessaoDoIdp: saida.comSessaoDoIdp,
      accessToken: saida.accessToken,
    });
  }

  if (destino.tipo === 'franquias') {
    const base = `${(saida.franquiasUrl ?? '').replace(/\/+$/, '')}/`;
    if (saida.comSessaoDoIdp) return comMarcaDeSso(base);
    if (!saida.accessToken) return base;
    return `${base}?token=${saida.accessToken}`;
  }

  return undefined;
}
